import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCheckDuplicateDocument, useCreateDocument, useDocuments, useDownloadDocument } from '../hooks/useQueries';
import Modal from './Popup/Modal';
import DuplicateDocumentModal from './Popup/DuplicateDocumentModal';
import Toast from './Popup/Toast';
import './AttachmentsUpload.css';

const AttachmentsUpload = ({ itemId, readOnly = false }) => {
  const [uploadModal, setUploadModal] = useState({ show: false, file: null, notes: '' });
  const [duplicateModal, setDuplicateModal] = useState({ show: false, existingDocumentId: null, filename: '' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isUploadDragActive, setIsUploadDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const createDocumentMutation = useCreateDocument();
  const duplicateCheckQuery = useCheckDuplicateDocument(itemId, uploadModal.file?.name || '', { enabled: false });
  const { data: { documents = [] } = {}, isLoading: docsLoading } = useDocuments(itemId);
  const downloadDocument = useDownloadDocument();

  const renameFileWithTimestamp = (file) => {
    if (!file || typeof file.name !== 'string') return file;

    const name = file.name;
    const dotIndex = name.lastIndexOf('.');
    const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
    const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const newName = `${base}-${stamp}${ext}`;

    try {
      return new File([file], newName, { type: file.type, lastModified: file.lastModified });
    } catch (e) {
      return file;
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const handleUploadClick = () => {
    setUploadModal({ show: true, file: null, notes: '' });
    setIsUploadDragActive(false);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      showToast('File size must be less than 50MB', 'error');
      return;
    }

    setUploadModal(prev => ({ ...prev, file }));

    // Allow selecting the same file again
    event.target.value = '';
  };

  const handleSelectedFile = (file) => {
    if (!file) return;

    // Check file size (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      showToast('File size must be less than 50MB', 'error');
      return;
    }

    setUploadModal(prev => ({ ...prev, file }));
  };

  const handleUploadDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(true);
  };

  const handleUploadDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(true);
  };

  const handleUploadDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(false);
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUploadDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    handleSelectedFile(file);
  };

  const handleConfirmUpload = async () => {
    if (!uploadModal.file) {
      return;
    }

    // Check for duplicates by displayed filename before uploading
    try {
      const duplicateResult = await duplicateCheckQuery.refetch();
      if (duplicateResult?.data?.exists && duplicateResult?.data?.document?.id) {
        setDuplicateModal({
          show: true,
          existingDocumentId: duplicateResult.data.document.id,
          filename: uploadModal.file.name
        });
        return;
      }
    } catch (error) {
      console.error('Duplicate check failed:', error);
      showToast(error.message || 'Failed to check for duplicate documents', 'error');
      return;
    }

    try {
      await createDocumentMutation.mutateAsync({
        itemId: itemId,
        file: uploadModal.file,
        notes: uploadModal.notes
      });
      showToast('Document uploaded successfully');
      setUploadModal({ show: false, file: null, notes: '' });
    } catch (error) {
      console.error('Upload error:', error);
      showToast(error.message || 'Failed to upload document', 'error');
    }
  };

  const handleDuplicateAddAsVersion = async () => {
    try {
      await createDocumentMutation.mutateAsync({
        itemId,
        file: uploadModal.file,
        notes: uploadModal.notes,
        isNewVersion: true,
        existingDocumentId: duplicateModal.existingDocumentId,
      });
      showToast('New version uploaded successfully');
      setDuplicateModal({ show: false, existingDocumentId: null, filename: '' });
      setUploadModal({ show: false, file: null, notes: '' });
    } catch (error) {
      console.error('Upload new version error:', error);
      showToast(error.message || 'Failed to upload new version', 'error');
    }
  };

  const handleDuplicateUploadAsNewDocument = async () => {
    try {
      const renamedFile = renameFileWithTimestamp(uploadModal.file);
      await createDocumentMutation.mutateAsync({
        itemId,
        file: renamedFile,
        notes: uploadModal.notes,
      });
      showToast('Document uploaded successfully (renamed)');
      setDuplicateModal({ show: false, existingDocumentId: null, filename: '' });
      setUploadModal({ show: false, file: null, notes: '' });
    } catch (error) {
      console.error('Upload renamed document error:', error);
      showToast(error.message || 'Failed to upload document', 'error');
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'bi-file-image';
    if (mimeType === 'application/pdf') return 'bi-file-pdf';
    return 'bi-file-earmark';
  };

  const handleDownload = async (document) => {
    try {
      await downloadDocument(document.id, document.filename);
    } catch (error) {
      showToast(error.message || 'Failed to download document', 'error');
    }
  };

  if (readOnly) {
    return (
      <div className="attachments-upload readonly">
        <div className="readonly-message">
          <i className="bi bi-info-circle"></i>
          Attachment upload is not available in read-only mode
        </div>
      </div>
    );
  }

  return (
    <div className="attachments-upload">
      <div className="attachments-upload-container">
        <button 
          className="btn btn-primary attachments-upload-btn"
          onClick={handleUploadClick}
          disabled={createDocumentMutation.isPending}
          title="Upload attachments for this item"
        >
          <i className="bi bi-paperclip"></i>
          {createDocumentMutation.isPending ? 'Uploading...' : 'Upload Attachments'}
        </button>
        <div className="attachments-help-text">
          Upload documents and files for this item
        </div>
        {/* Mini list of uploaded documents */}
        <div className="attachments-mini-list">
          {docsLoading ? (
            <div className="mini-list-loading">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="mini-list-empty">No attachments uploaded yet.</div>
          ) : (
            <>
              <ul className="mini-list">
                {documents.slice(0, 5).map(doc => (
                  <li key={doc.id} className="mini-list-item">
                    <span className="mini-list-filename">
                      <i className={`bi ${getFileIcon(doc.mime_type)} mini-list-icon`}></i>
                      {doc.filename.length > 30 ? doc.filename.substring(0, 30) + '...' : doc.filename}
                    </span>
                    <span className="mini-list-date">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                    <button className="btn btn-link btn-sm mini-list-download" title="Download" onClick={() => handleDownload(doc)}>
                      <i className="bi bi-download"></i>
                    </button>
                  </li>
                ))}
              </ul>
              {documents.length > 5 && (
                <div className="mini-list-more">+{documents.length - 5} more...</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {uploadModal.show && createPortal(
        <Modal
          title="Upload Attachment"
          show={uploadModal.show}
          onCancel={() => {
            setUploadModal({ show: false, file: null, notes: '' });
            setIsUploadDragActive(false);
          }}
          onConfirm={handleConfirmUpload}
          confirmText="Upload"
          confirmDisabled={!uploadModal.file || createDocumentMutation.isPending}
          size="md"
          content={
            <div className="upload-container">
              <div 
                className={`upload-area ${uploadModal.file ? 'has-file' : ''} ${isUploadDragActive ? 'drag-active' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleUploadDragEnter}
                onDragOver={handleUploadDragOver}
                onDragLeave={handleUploadDragLeave}
                onDrop={handleUploadDrop}
              >
                <input
                  id="attachments-file-input"
                  type="file"
                  accept="*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                />
                {!uploadModal.file ? (
                  <>
                    <div className="upload-icon">
                      <i className="bi bi-cloud-upload"></i>
                    </div>
                    <div className="upload-text">
                      Click or drag & drop a file
                    </div>
                    <div className="upload-text" style={{ fontSize: '12px', color: '#94a3b8' }}>
                      All file types supported (max 50MB)
                    </div>
                  </>
                ) : (
                  <div className="file-info">
                    <i className={`${getFileIcon(uploadModal.file.type)} file-info-icon`}></i>
                    <div className="file-info-details">
                      <div className="file-info-name">{uploadModal.file.name}</div>
                      <div className="file-info-size">
                        {(uploadModal.file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button 
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadModal(prev => ({ ...prev, file: null }));
                      }}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group mt-3">
                <label htmlFor="upload-notes">Notes (optional)</label>
                <textarea
                  id="upload-notes"
                  className="form-control"
                  rows="3"
                  placeholder="Add optional notes about this attachment..."
                  value={uploadModal.notes}
                  onChange={(e) => setUploadModal(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
          }
        />,
        document.body
      )}

      {/* Duplicate Document Modal */}
      {duplicateModal.show && createPortal(
        <DuplicateDocumentModal
          show={duplicateModal.show}
          filename={duplicateModal.filename}
          isLoading={createDocumentMutation.isPending}
          onCancel={() => setDuplicateModal({ show: false, existingDocumentId: null, filename: '' })}
          onAddAsNewVersion={handleDuplicateAddAsVersion}
          onUploadAsNewDocument={handleDuplicateUploadAsNewDocument}
        />,
        document.body
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />
      )}
    </div>
  );
};

export default AttachmentsUpload;